import { getApolloContacts } from '@api/apollo-enrichment-contact';
import { AppBarTitle } from '@components/app-bar-title';
import { createContextQuick } from '@components/quick-context';
import { RiSearchFill } from '@icon/react-icons';
import { QueryParamsAdapter, useAtomicTrigger, useMountedGetter } from '@lib/common';
import { IconButton } from '@material-ui/core';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { filter, map } from 'rxjs/operators';
import { columns } from './columns';
import { EnhancedTable } from './table/mtable';

export const [RefreshCounterProvider, useRefreshCounter] = createContextQuick<{
  refreshCounter: number;
  forceRefresh: () => any;
}>();

export default function ApolloEnrichedContacts() {
  const [refreshCounter, setRefreshCounter] = useState(0);
  const { loading, data, trigger: fetchData, pageCount, rowCount } = useServerData();
  const location = useLocation();
  const initial: any = useMemo(() => {
    if (!location.search) {
      return null;
    }
    const params = QueryParamsAdapter.fromParams(location.search);
    if (params && params.sortBy)
      (params.sortBy as Array<Object>).forEach(
        x => (x['desc'] = x['desc'] === 'false' ? false : true)
      );
    return {
      ...params,
      ...(params.pageIndex ? { pageIndex: +params.pageIndex } : {}),
      ...(params.pageSize ? { pageSize: +params.pageSize } : {}),
    };
  }, []);

  useEffect(() => {
    if (initial) {
      initial.pageSize = 10;
      initial.pageIndex = 0;
    }
  }, []);

  return (
    <>
      <AppBarTitle>
        <IconButton color="inherit">
          <RiSearchFill />
        </IconButton>{' '}
        Apollo Enriched Contacts (DELI)
      </AppBarTitle>
      <RefreshCounterProvider
        refreshCounter={refreshCounter}
        forceRefresh={() => setRefreshCounter(counter => counter + 1)}>
        <EnhancedTable
          loading={loading}
          getSubRows={<></>}
          pageCount={pageCount}
          rowCount={rowCount}
          initial={initial}
          columns={columns}
          updateData={fetchData}
          data={data}
        />
      </RefreshCounterProvider>
    </>
  );
}

function useServerData() {
  const history = useHistory();
  const isMounted = useMountedGetter();

  const {
    loading,
    error,
    result: { count = 0, data = [], page_size = 25 } = {},
    trigger,
  } = useAtomicTrigger(({ sortBy, pageSize, pageIndex, filters }, goToPage) => {
    const nextState = { sortBy, pageSize, pageIndex, filters };
    const defaultState = { sortBy: [], pageSize: 25, pageIndex: 0, filters: [] };
    const state = Object.fromEntries(
      Object.entries(nextState).filter(([k, v]) => !isEqual(defaultState[k], v))
    );
    const shouldClear = !Object.keys(state).length;

    history.replace({
      search: shouldClear ? '' : '?' + QueryParamsAdapter.toParams(state).toString(),
    });

    return getApolloContacts({ sortBy, pageSize, page: pageIndex + 1, filters }).pipe(
      filter(d => {
        if (d.data && d.data.count && !d.data.data.length) {
          goToPage(0);
          return false;
        }
        return true;
      }),
      map(d => d.data),
      filter(isMounted)
    );
  });

  return {
    loading,
    error,
    rowCount: count,
    pageCount: Math.ceil(count / page_size),
    data,
    trigger,
  } as const;
}
